import { supabase } from '../supabase/client';
import { mengenDifferenz } from './mengen';
import { aktuelleZeile, type EinheitEigZeitraum } from './einheitEigentum';

/**
 * Verknüpft ein Objekt mit einem Eigentümer in `objekt_eigentuemer` — der Tabelle,
 * die die KI-Analyse (email-analyse) liest. Idempotent (legt nicht doppelt an).
 *
 * Hintergrund: Die UI-Modals schreiben Eigentümer nur nach `entity_links` /
 * `objekte.eigentuemer_id`. Die KI liest aber `objekt_eigentuemer`. Ohne diesen
 * zusätzlichen Schreibweg blieben manuell angelegte Eigentümer für die KI unsichtbar.
 *
 * Gibt `neuVerknuepft` zurück: `false`, wenn die Zeile bereits existierte (reine
 * Re-Auswahl eines schon verknüpften Objekts), `true` nach echtem Insert. Aufrufer
 * nutzen das, um den Eigentümerwechsel-Hook (Ordner anlegen + Vorbesitzer-Wechsel
 * planen) nur bei tatsächlich neuer Verknüpfung auszulösen.
 */
export async function setzeObjektEigentuemer(objektId: string, eigentuemerId: string, vertragAb?: string): Promise<{ neuVerknuepft: boolean }> {
  const { data } = await supabase
    .from('objekt_eigentuemer')
    .select('objekt_id')
    .eq('objekt_id', objektId)
    .eq('eigentuemer_id', eigentuemerId)
    .maybeSingle();
  if (data) return { neuVerknuepft: false };

  const { error } = await supabase.from('objekt_eigentuemer').insert({
    objekt_id: objektId,
    eigentuemer_id: eigentuemerId,
    vertrag_ab: vertragAb || new Date().toISOString().slice(0, 10),
  });
  if (error) throw new Error(error.message);
  return { neuVerknuepft: true };
}

/**
 * Setzt die KOMPLETTE Eigentümerliste eines Objekts — für die Objekt-Formulare, die
 * mehrere Eigentümer auf einmal pflegen. Schreibt nur die Differenz: bestehende
 * Verknüpfungen bleiben samt `vertrag_ab` unangetastet.
 *
 * Vorher setzten diese Formulare `entity_links` per „alles löschen, neu anlegen" und
 * schrieben die Anzeigefelder auf `objekte` direkt — beides an `objekt_eigentuemer`
 * vorbei, das die KI liest. Genau daraus entstanden die auseinanderlaufenden Bestände
 * (Befund 17.08.2026).
 */
export async function setzeObjektEigentuemerListe(objektId: string, eigentuemerIds: string[]): Promise<void> {
  const { data, error } = await supabase
    .from('objekt_eigentuemer').select('eigentuemer_id').eq('objekt_id', objektId);
  if (error) throw new Error(error.message);

  const { hinzu, weg } = mengenDifferenz((data || []).map((r) => r.eigentuemer_id as string), eigentuemerIds);

  if (weg.length > 0) {
    const { error: delErr } = await supabase
      .from('objekt_eigentuemer').delete().eq('objekt_id', objektId).in('eigentuemer_id', weg);
    if (delErr) throw new Error(delErr.message);
  }
  if (hinzu.length > 0) {
    const heute = new Date().toISOString().slice(0, 10);
    const { error: insErr } = await supabase
      .from('objekt_eigentuemer')
      .insert(hinzu.map((eigentuemer_id) => ({ objekt_id: objektId, eigentuemer_id, vertrag_ab: heute })));
    if (insErr) throw new Error(insErr.message);
  }
}

/**
 * Setzt/aktualisiert den Eigentümer einer Einheit (WEG/SEV) in `einheit_eigentuemer`.
 * Wie bisher (Upsert auf einheit_id): die laufende Hauptzeile der Einheit wird ersetzt –
 * das Formular der Einheit korrigiert „den“ Eigentümer. Ein Eigentümerwechsel mit
 * Historie läuft über „Neuer Eigentümer“ (lib/eigentuemer/neuerEigentuemerSpeichern.ts).
 */
export async function setzeEinheitEigentuemer(
  einheitId: string,
  eigentuemerId: string,
  felder?: { mea?: number | null; hausgeld_vorschuss?: number | null; ruecklage_vorschuss?: number | null; sev_status?: string | null },
): Promise<void> {
  const werte = {
    eigentuemer_id: eigentuemerId,
    mea: felder?.mea ?? null,
    hausgeld_vorschuss: felder?.hausgeld_vorschuss ?? null,
    ruecklage_vorschuss: felder?.ruecklage_vorschuss ?? null,
    sev_status: felder?.sev_status ?? null,
    updated_at: new Date().toISOString(),
  };
  const { data, error } = await supabase.from('einheit_eigentuemer')
    .select('id, eigentuemer_id, mea, hausgeld_vorschuss, vertrag_ab, vertrag_bis').eq('einheit_id', einheitId);
  if (error) throw new Error(error.message);
  const zeilen = (data ?? []) as ({ id: string; eigentuemer_id: string } & EinheitEigZeitraum & { mea: unknown; hausgeld_vorschuss: unknown })[];
  // gibt es schon eine Zeile dieses Eigentümers, wird sie aktualisiert (UNIQUE einheit_id + eigentuemer_id)
  const ziel = zeilen.find(z => z.eigentuemer_id === eigentuemerId) ?? aktuelleZeile(zeilen, new Date().toISOString().slice(0, 10));
  const { error: schreibFehler } = ziel
    ? await supabase.from('einheit_eigentuemer').update(werte).eq('id', ziel.id)
    : await supabase.from('einheit_eigentuemer').insert({ einheit_id: einheitId, ...werte });
  if (schreibFehler) throw new Error(schreibFehler.message);
}

/** Entfernt den Eigentümer-Eintrag einer Einheit – nur die Zeile dieses Eigentümers, die Historie bleibt. */
export async function entferneEinheitEigentuemer(einheitId: string, eigentuemerId: string): Promise<void> {
  const { error } = await supabase.from('einheit_eigentuemer').delete().eq('einheit_id', einheitId).eq('eigentuemer_id', eigentuemerId);
  if (error) throw new Error(error.message);
}
